import React from 'react'

import { DurationTestStatus } from '@/api/definition'
import type { Device } from '@/pages/Device/Device'

// Reducer types and initial state
export interface SelectedDevicesState {
  selectedDevices: Device[]
  isLoading: boolean
  error: string | null
  activeStep: number
  action: number
  deviceDetails: Device[]
  requestStatuses: Map<
    string,
    { status: 'success' | 'error' | 'loading'; message?: string }
  >
}

export const initialSelectedDevicesState: SelectedDevicesState = {
  selectedDevices: [],
  isLoading: false,
  error: null,
  activeStep: 1,
  action: DurationTestStatus.DurationTestNotStarted,
  deviceDetails: [],
  requestStatuses: new Map(),
}

export type SelectedDevicesAction =
  | { type: 'SET_SELECTED_DEVICES'; payload: Device[] }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_ACTIVE_STEP'; payload: number }
  | { type: 'SET_ACTION'; payload: number }
  | { type: 'SET_DEVICE_DETAILS'; payload: Device[] }
  | {
      type: 'SET_REQUEST_STATUS'
      payload: {
        uid: string
        status: { status: 'success' | 'error' | 'loading'; message?: string }
      }
    }
  | { type: 'RESET' }

export const selectedDevicesReducer = (
  state: SelectedDevicesState,
  action: SelectedDevicesAction,
): SelectedDevicesState => {
  switch (action.type) {
    case 'SET_SELECTED_DEVICES':
      return { ...state, selectedDevices: action.payload }
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload }
    case 'SET_ERROR':
      return { ...state, error: action.payload }
    case 'SET_ACTIVE_STEP':
      return { ...state, activeStep: action.payload }
    case 'SET_ACTION':
      return { ...state, action: action.payload }
    case 'SET_DEVICE_DETAILS':
      return { ...state, deviceDetails: action.payload }
    case 'SET_REQUEST_STATUS': {
      const newStatuses = new Map(state.requestStatuses)
      newStatuses.set(action.payload.uid, action.payload.status)
      return { ...state, requestStatuses: newStatuses }
    }
    case 'RESET':
      return initialSelectedDevicesState
    default:
      return state
  }
}

// Context for sharing state between components
export interface SelectedDevicesContextType {
  state: SelectedDevicesState
  dispatch: React.Dispatch<SelectedDevicesAction>
  onStartDurationTest: () => Promise<void>
  onSkipDurationTest: () => Promise<void>
  onBackToStep1: () => void
}

export const SelectedDevicesContext =
  React.createContext<SelectedDevicesContextType | null>(null)
